import * as R from 'ramda'
import { datadogLogs } from '@datadog/browser-logs'

import { setToast } from 'store/Notification'
import { sanitizePayloadForLogging } from 'utils/monitoring/sanitizePayload'

/**
 * If an API or form error action gets dispatched, this middleware will catch
 * that and add an error event to the dataLayer for any analytics code to grab.
 */
export default ({ dispatch }) =>
  next =>
  (action = {}) => {
    if (!action.payload) return next(action)
    const { error, type, payload, meta } = action

    // Parse error API responses. Meta information here
    // will include the trace-id for backend debugging.
    if (/(ERROR$)/.test(type) && payload.response) {
      reportError({ type, meta, payload })

      // Log API errors to Datadog
      // Note: endpoint is populated by endpointInterceptor middleware
      // Response is sanitized to remove PHI/PII before logging
      datadogLogs.logger.error(`API Response Error: ${type}`, {
        actionType: type,
        traceId: meta?.traceId,
        uuid: meta?.uuid,
        httpStatus: payload.status, // Renamed from 'status' to avoid overriding Datadog's status field
        endpoint: meta?.endpoint,
        response: sanitizePayloadForLogging(payload),
      })

      return next(action)
    }

    // Send form errors after submitted
    if (/^@@redux-form\/STOP_SUBMIT$/.test(type) && !!error) {
      reportError({ type, meta, payload })

      // Log form errors to Datadog
      datadogLogs.logger.error('Form Validation Error', {
        actionType: type,
        formName: meta?.form,
      })

      return next(action)
    }

    // catches errors if an API service is down, these
    // look like CORS errors, but are not.
    if (payload.name === 'RequestError' && /REQUEST/.test(type)) {
      reportError({ type, meta, payload })

      // Log network/service errors to Datadog
      // Note: endpoint is populated by endpointInterceptor middleware
      datadogLogs.logger.error(`Network Request Failed: ${type}`, {
        actionType: type,
        errorName: payload.name,
        errorMessage: payload.message,
        traceId: meta?.traceId,
        uuid: meta?.uuid,
        endpoint: meta?.endpoint,
        isSmartLayer: /SMART_LAYER/.test(type),
      })

      // Don't show toast for Smart Layer errors since we handle them gracefully
      // with fallback to basic search
      if (!/SMART_LAYER/.test(type)) {
        dispatch(
          setToast(
            `Sorry, we're currently experiencing a minor service error so some features or information may not be available.`,
            'risk',
          ),
        )
      }
    }

    return next(action)
  }

// helper function to report the error to the dataLayer
export const reportError = error => {
  error.occuredAt = new Date().toISOString()

  if (window && Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: 'userErrorEvent',
      userErrorData: JSON.stringify(
        // we want to make sure we filter PII and PHI info
        // out of the meta layer that way we're not sending
        // that data to Google or to our slack channel
        {
          ...error,
          meta: R.either(R.isEmpty, R.isNil)(error.meta)
            ? {}
            : R.pick(['traceId', 'uuid'], error.meta),
        },
        '',
        2,
      ),
    })
  }
}
